/**
 * EF-CLIENT-PORTAL-UI-V1
 * Client Documents Page
 * 
 * Features:
 * - Documents grouped by project
 * - Filter by type and status
 * - Server-side data (no frontend filtering)
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import {
  FileText,
  Loader2,
  Download,
  Filter,
  ExternalLink,
  Receipt,
  FileCheck,
  File,
  ChevronDown,
  ChevronUp,
  Building2,
  Eye,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function ClientDocumentsPage() {
  const navigate = useNavigate();
  const { getToken } = useOutletContext();
  // EVENTSFLO-PORTAL-I18N-PHASE-2B-CLIENT-PORTAL-COVERAGE-V1
  const { t } = useTranslation(["clientPortal", "common"]);

  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [byProject, setByProject] = useState({});
  const [byType, setByType] = useState({});
  const [meta, setMeta] = useState({ page: 1, limit: 20, total: 0, total_pages: 0 });
  const [typeFilter, setTypeFilter] = useState("all");
  const [expandedProjects, setExpandedProjects] = useState(new Set());

  const fetchDocuments = useCallback(async (docType = "all") => {
    const token = getToken();
    if (!token) return;

    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (docType !== "all") {
        params.append("document_type", docType);
      }

      const response = await fetch(`${API_URL}/api/client/documents?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to fetch documents");

      const data = await response.json();
      setDocuments(data.data || []);
      setByProject(data.by_project || {});
      setByType(data.by_type || {});
      setMeta(data.meta || { page: 1, limit: 20, total: 0, total_pages: 0 });
      
      // Expand all projects by default
      if (data.by_project) {
        setExpandedProjects(new Set(Object.keys(data.by_project)));
      }
    } catch (err) {
      console.error("Documents fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    fetchDocuments(typeFilter);
  }, [fetchDocuments, typeFilter]);

  const toggleProject = (projectId) => {
    setExpandedProjects((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(projectId)) {
        newSet.delete(projectId);
      } else {
        newSet.add(projectId);
      }
      return newSet;
    });
  };

  const getTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case "invoice":
        return Receipt;
      case "quote":
        return FileCheck;
      default:
        return File;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "paid":
        return "bg-green-500/10 text-green-500 border-green-500/20";
      case "sent":
        return "bg-blue-500/10 text-blue-500 border-blue-500/20";
      case "draft":
        return "bg-yellow-500/10 text-yellow-500 border-yellow-500/20";
      case "overdue":
        return "bg-red-500/10 text-red-500 border-red-500/20";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  const formatCurrency = (amount, currency = "EUR") => {
    if (amount === null || amount === undefined) return null;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
    }).format(amount);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return null;
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
  };

  // Get unique project names from documents for display
  const projectNames = {};
  documents.forEach((doc) => {
    if (doc.project_id && doc.project_name) {
      projectNames[doc.project_id] = doc.project_name;
    }
  });

  // EVENTSFLO-COMPANY-DOCUMENTS-CLIENT-PORTAL-DOWNLOADS-V1
  // Company-wide documents tab — tenant-published docs visible to this client.
  const [companyDocs, setCompanyDocs] = useState([]);
  const [companyLoading, setCompanyLoading] = useState(true);
  const fetchCompanyDocs = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    setCompanyLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/client/company-documents`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error("fetch failed");
      const data = await r.json();
      setCompanyDocs(data.items || []);
    } catch {
      setCompanyDocs([]);
    } finally {
      setCompanyLoading(false);
    }
  }, [getToken]);
  useEffect(() => { fetchCompanyDocs(); }, [fetchCompanyDocs]);

  const downloadCompanyDoc = async (doc) => {
    const token = getToken();
    try {
      const r = await fetch(
        `${API_URL}/api/client/company-documents/${doc.id}/download`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!r.ok) throw new Error(`status ${r.status}`);
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.display_filename || doc.title || "document";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error(t("clientPortal:documents.downloadFailed"));
    }
  };

  // EVENTSFLO-COMPANY-DOCUMENTS-CLIENT-PDF-INLINE-VIEW-V1
  // Open the PDF in a new browser tab via an authorised fetch. We MUST use
  // fetch (not <a target=_blank>) because the endpoint requires the
  // Authorization header.
  const isPdfDoc = (d) =>
    (d?.mime_type || "").toLowerCase() === "application/pdf";
  const viewCompanyDocPdf = async (doc) => {
    const token = getToken();
    try {
      const r = await fetch(
        `${API_URL}/api/client/company-documents/${doc.id}/view`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!r.ok) throw new Error(`status ${r.status}`);
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const w = window.open(url, "_blank", "noopener,noreferrer");
      if (!w) {
        // Popup blocked — fall back to same-tab navigation.
        window.location.href = url;
      }
      // Best-effort cleanup once the tab has rendered the PDF.
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch {
      toast.error(t("clientPortal:documents.openFailed"));
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold" data-testid="documents-title">{t("clientPortal:documents.title")}</h1>
        <p className="text-sm text-muted-foreground">
          {t("clientPortal:documents.subtitle")}
        </p>
      </div>

      <Tabs defaultValue="project" className="w-full">
        <TabsList>
          <TabsTrigger value="project" data-testid="docs-tab-project">
            {t("clientPortal:documents.tabs.project")}
          </TabsTrigger>
          <TabsTrigger value="company" data-testid="docs-tab-company">
            <Building2 className="h-3.5 w-3.5 mr-1" />
            {t("clientPortal:documents.tabs.company")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="project" className="space-y-4 mt-4">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm text-muted-foreground">
              {t("clientPortal:documents.totalLabel", { count: meta.total })}
            </p>
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-[150px]" data-testid="document-type-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("clientPortal:documents.typeFilter.all")}</SelectItem>
                  <SelectItem value="quote">{t("clientPortal:documents.typeFilter.quote")}</SelectItem>
                  <SelectItem value="invoice">{t("clientPortal:documents.typeFilter.invoice")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Type summary badges */}
          {Object.keys(byType).length > 0 && (
            <div className="flex flex-wrap gap-2">
              {Object.entries(byType).map(([type, count]) => (
                <Badge key={type} variant="outline" className="capitalize">
                  {type}: {count}
                </Badge>
              ))}
            </div>
          )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : documents.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">{t("clientPortal:documents.empty")}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {/* Group by project */}
          {Object.entries(byProject).map(([projectId, projectDocs]) => (
            <Card key={projectId} data-testid={`project-docs-${projectId}`}>
              <CardHeader
                className="cursor-pointer py-3"
                onClick={() => toggleProject(projectId)}
              >
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    <span
                      className="cursor-pointer hover:text-primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/client/projects/${projectId}`);
                      }}
                    >
                      {projectNames[projectId] || t("clientPortal:documents.unknownProject")}
                    </span>
                    <Badge variant="secondary" className="ml-2">
                      {projectDocs.length}
                    </Badge>
                  </CardTitle>
                  {expandedProjects.has(projectId) ? (
                    <ChevronUp className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  )}
                </div>
              </CardHeader>

              {expandedProjects.has(projectId) && (
                <CardContent className="pt-0">
                  {/* EF-CLIENT-PORTAL-UI-REFINEMENT-V1: Clearer document list with visible download */}
                  <div className="divide-y divide-border">
                    {projectDocs.map((doc) => {
                      const TypeIcon = getTypeIcon(doc.type);
                      return (
                        <div
                          key={doc.id}
                          className="flex items-center gap-3 py-3 first:pt-0 last:pb-0"
                          data-testid={`document-${doc.id}`}
                        >
                          {/* Type icon */}
                          <div className="shrink-0 w-10 h-10 rounded-lg bg-accent/50 flex items-center justify-center">
                            <TypeIcon className="h-5 w-5 text-muted-foreground" />
                          </div>
                          
                          {/* Document info */}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-0.5">
                              <p className="font-medium text-sm truncate">{doc.name}</p>
                              <Badge variant="outline" className={`text-[10px] ${getStatusColor(doc.status)}`}>
                                {doc.status}
                              </Badge>
                            </div>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                              <span className="capitalize">{doc.type}</span>
                              {doc.amount !== null && (
                                <span className="font-medium text-foreground">
                                  {formatCurrency(doc.amount, doc.currency)}
                                </span>
                              )}
                              {doc.due_date && (
                                <span>{t("clientPortal:documents.duePrefix", { date: doc.due_date })}</span>
                              )}
                            </div>
                          </div>
                          
                          {/* Download button - always visible */}
                          {doc.file_url && (
                            <Button
                              variant="outline"
                              size="sm"
                              asChild
                              className="shrink-0 gap-1.5"
                            >
                              <a
                                href={doc.file_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`download-${doc.id}`}
                              >
                                <Download className="h-3.5 w-3.5" />
                                <span className="hidden sm:inline">{t("clientPortal:documents.viewAction")}</span>
                              </a>
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}
        </TabsContent>

        {/* EVENTSFLO-COMPANY-DOCUMENTS-CLIENT-PORTAL-DOWNLOADS-V1 */}
        <TabsContent value="company" className="space-y-4 mt-4">
          {companyLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : companyDocs.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center" data-testid="company-docs-empty-client">
                <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">
                  No documents are currently available.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {companyDocs.map((d) => (
                <Card key={d.id} data-testid={`client-company-doc-${d.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-medium text-foreground truncate">{d.title}</h3>
                          <Badge variant="outline" className="capitalize text-[10px]">
                            {String(d.category || "other").replace("_", " ")}
                          </Badge>
                          {d.version && (
                            <Badge variant="outline" className="text-[10px]">
                              v{d.version}
                            </Badge>
                          )}
                        </div>
                        {d.description && (
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                            {d.description}
                          </p>
                        )}
                        {d.expires_at && (
                          <p className="text-xs text-amber-500 mt-1">
                            {t("clientPortal:documents.expiresAt", { date: d.expires_at })}
                          </p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => downloadCompanyDoc(d)}
                        className="shrink-0 gap-1.5"
                        data-testid={`client-company-doc-download-${d.id}`}
                      >
                        <Download className="h-3.5 w-3.5" />
                        <span className="hidden sm:inline">{t("clientPortal:documents.downloadAction")}</span>
                      </Button>
                    </div>
                    {/* EVENTSFLO-COMPANY-DOCUMENTS-CLIENT-PDF-INLINE-VIEW-V1
                        View PDF button only for application/pdf documents. */}
                    {isPdfDoc(d) && (
                      <div className="mt-2 flex justify-end">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => viewCompanyDocPdf(d)}
                          className="gap-1.5"
                          data-testid={`client-company-doc-view-${d.id}`}
                        >
                          <Eye className="h-3.5 w-3.5" />
                          <span>{t("clientPortal:documents.viewPdfAction")}</span>
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
